import React, { useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProjectProvider } from './context/ProjectContext';
import LoginPage from './pages/LoginPage';
// NAV-FE-1b — the two redirect components stay eager (decision of the train).
// They live INSIDE their pages' files, so those two pages ride in `main` until
// the redirects move to a file of their own.
import { QualitySetupRedirect } from './pages/QualitySetupPage';
import { HandoverInspectionExecuteRedirect } from './pages/HandoverInspectionPage';
import lazyWithReload from './lib/lazyWithReload';
import RouteFallback from './components/RouteFallback';
import './App.css';

// NAV-FE-1b — every page below loads with its route: `main` carries the shell
// and the login, and each page is its own chunk, fetched the first time it is
// opened (and cached after). `lazyWithReload` reloads once if a deploy removed
// the chunk a stale tab asks for. LoginPage stays eager: it is the first paint.
const DashboardPage = lazyWithReload(() => import('./pages/DashboardPage'), 'DashboardPage');
const ProjectDetailPage = lazyWithReload(() => import('./pages/ProjectDetailPage'), 'ProjectDetailPage');
const DrawingViewerPage = lazyWithReload(() => import('./pages/DrawingViewerPage'), 'DrawingViewerPage');
// NL22-A — `InspectionPage` / `InspectionListPage` / `NewInspectionPage` (the
// legacy `inspections` collection) are DELETED. `InspectionsPage` below is the
// LIVING bookings surface (`/inspection-bookings`) and is unrelated to them.
const InspectionsPage = lazyWithReload(() => import('./pages/InspectionsPage'), 'InspectionsPage');
const InspectionRegisterPage = lazyWithReload(() => import('./pages/InspectionRegisterPage'), 'InspectionRegisterPage');
// NCR-2 — the NCR by company: the project's list, the NCR page, the raise.
const NcrListPage = lazyWithReload(() => import('./pages/NcrListPage'), 'NcrListPage');
const NcrPage = lazyWithReload(() => import('./pages/NcrPage'), 'NcrPage');
const NcrRaisePage = lazyWithReload(() => import('./pages/NcrRaisePage'), 'NcrRaisePage');
// NAV-FE-3b — the project's RFI list (the RFI itself opens at /rfis/:rfiId).
const RfiListPage = lazyWithReload(() => import('./pages/RfiListPage'), 'RfiListPage');
const ProjectSettingsPage = lazyWithReload(() => import('./pages/ProjectSettingsPage'), 'ProjectSettingsPage');
const LotDetail = lazyWithReload(() => import('./pages/LotDetail'), 'LotDetail');
const AssetPage = lazyWithReload(() => import('./pages/AssetPage'), 'AssetPage');
// DOSSIER-FE-1 — the asset's Handover Dossier page (v21).
const AssetDossierPage = lazyWithReload(() => import('./pages/AssetDossierPage'), 'AssetDossierPage');
const ITPDetailPage = lazyWithReload(() => import('./pages/ITPDetailPage'), 'ITPDetailPage');
const StructuredITPTemplateEditorPage = lazyWithReload(() => import('./pages/StructuredITPTemplateEditorPage'), 'StructuredITPTemplateEditorPage');
const StructuredITPsPage = lazyWithReload(() => import('./pages/StructuredITPsPage'), 'StructuredITPsPage');
const StructuredITCDetailPage = lazyWithReload(() => import('./pages/StructuredITCDetailPage'), 'StructuredITCDetailPage');
const ThreadDetailPage = lazyWithReload(() => import('./pages/ThreadDetailPage'), 'ThreadDetailPage');
const EntityThreadRedirect = lazyWithReload(() => import('./pages/EntityThreadRedirect'), 'EntityThreadRedirect');
const ProjectDocumentsPage = lazyWithReload(() => import('./pages/ProjectDocumentsPage'), 'ProjectDocumentsPage');
const TestRegisterPage = lazyWithReload(() => import('./pages/TestRegisterPage'), 'TestRegisterPage');
const QualitySetupPage = lazyWithReload(() => import('./pages/QualitySetupPage'), 'QualitySetupPage');
const TestRequestDetailPage = lazyWithReload(() => import('./pages/TestRequestDetailPage'), 'TestRequestDetailPage');
// LAB-FE-1 — the laboratory's portal (no app account; link + e-mail + code).
const LabPortalPage = lazyWithReload(() => import('./pages/LabPortalPage'), 'LabPortalPage');
const LabPortalRequestPage = lazyWithReload(() => import('./pages/LabPortalRequestPage'), 'LabPortalRequestPage');
const QualityBreakdownReviewPage = lazyWithReload(() => import('./pages/QualityBreakdownReviewPage'), 'QualityBreakdownReviewPage');
const QbGeometryReviewPage = lazyWithReload(() => import('./pages/QbGeometryReviewPage'), 'QbGeometryReviewPage');
const ExternalThreadPage = lazyWithReload(() => import('./pages/ExternalThreadPage'), 'ExternalThreadPage');
const AssetQATablePage = lazyWithReload(() => import('./pages/AssetQATablePage'), 'AssetQATablePage');
const HandoverPage = lazyWithReload(() => import('./pages/HandoverPage'), 'HandoverPage');
const HandoverInspectionPage = lazyWithReload(() => import('./pages/HandoverInspectionPage'), 'HandoverInspectionPage');
const PunchItemPage = lazyWithReload(() => import('./pages/PunchItemPage'), 'PunchItemPage');
const UserManagementPage = lazyWithReload(() => import('./pages/UserManagementPage'), 'UserManagementPage');
// INVITE-PAGE-1 — the invitation is a page of its own (mockup v5), next to the list.
const InviteUserPage = lazyWithReload(() => import('./pages/InviteUserPage'), 'InviteUserPage');
const CompaniesManagementPage = lazyWithReload(() => import('./pages/CompaniesManagementPage'), 'CompaniesManagementPage');
const ProfilePage = lazyWithReload(() => import('./pages/ProfilePage'), 'ProfilePage');
const AcceptInvitationPage = lazyWithReload(() => import('./pages/AcceptInvitationPage'), 'AcceptInvitationPage');
const ForgotPasswordPage = lazyWithReload(() => import('./pages/ForgotPasswordPage'), 'ForgotPasswordPage');
const ResetPasswordPage = lazyWithReload(() => import('./pages/ResetPasswordPage'), 'ResetPasswordPage');
// Mobile pages
const MobileLoginPage = lazyWithReload(() => import('./mobile/MobileLoginPage'), 'MobileLoginPage');
const MobileHomePage = lazyWithReload(() => import('./mobile/MobileHomePage'), 'MobileHomePage');
const MobileLotMapsPage = lazyWithReload(() => import('./mobile/MobileLotMapsPage'), 'MobileLotMapsPage');
const MobileLotMapViewPage = lazyWithReload(() => import('./mobile/MobileLotMapViewPage'), 'MobileLotMapViewPage');
const MobileAssetPage = lazyWithReload(() => import('./mobile/MobileAssetPage'), 'MobileAssetPage');
const MobilePhotoUploadPage = lazyWithReload(() => import('./mobile/MobilePhotoUploadPage'), 'MobilePhotoUploadPage');

// Detect if device is mobile (screen width < 768px or touch device)
const isMobileDevice = () => {
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isSmallScreen = window.innerWidth < 768;
  const isMobileUA = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  return (isSmallScreen && isTouchDevice) || isMobileUA;
};

// Mobile redirect component
const MobileRedirect = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    // Only redirect if on desktop routes and device is mobile
    if (isMobileDevice() && !location.pathname.startsWith('/m/') && !location.pathname.startsWith('/accept-invitation') && !location.pathname.startsWith('/reset-password')) {
      // Map desktop routes to mobile equivalents
      const path = location.pathname;
      if (path === '/login' || path === '/') {
        navigate('/m/login', { replace: true });
      } else if (path === '/dashboard') {
        navigate('/m', { replace: true });
      } else if (path.startsWith('/drawings/')) {
        const drawingId = path.split('/')[2];
        navigate(`/m/lotmap/${drawingId}`, { replace: true });
      }
      // NL22-A — the `/inspections/list` arm left with the desktop route it
      // mapped FROM. NL22-M then removed `/m/inspections` itself, so there is
      // no inspection arm on either side of this bridge any more.
      // For routes without mobile equivalents, stay on desktop version
    }
  }, [location.pathname, navigate]);
  
  return children;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Platform-Owner-only Route Component (redirects non-platform-owner to dashboard).
// Tenant-wide check; not project-scoped (no projectId in this context).
const PlatformOwnerRoute = ({ children }) => {
  const { isAuthenticated, loading, isPlatformOwner } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isPlatformOwner()) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Tenant Admin Route Component: platform_owner OR admin.
// Used for tenant-wide pages that admins are allowed to access (e.g. /users).
const TenantAdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user, isPlatformOwner } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // C3 — with a project in the URL the page is a DIRECTORY every member may
  // read (AUTH-A/C2 opened the server door to viewer+); the page itself
  // decides what that member gets to see. Without one it is the managers'
  // console, and the redirect stands.
  const hasProjectContext = new URLSearchParams(window.location.search).has('project_id');
  if (!hasProjectContext
      && !isPlatformOwner() && user?.role !== 'company_qa_admin' && user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Mobile Protected Route
const MobileProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-orange-500 border-t-transparent rounded-full" /></div>;
  if (!isAuthenticated) return <Navigate to="/m/login" replace />;
  return children;
};

// Mobile Public Route
const MobilePublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-orange-500 border-t-transparent rounded-full" /></div>;
  if (isAuthenticated) return <Navigate to="/m" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/accept-invitation"
        element={<AcceptInvitationPage />}
      />
      <Route
        path="/forgot-password"
        element={<ForgotPasswordPage />}
      />
      <Route
        path="/reset-password"
        element={<ResetPasswordPage />}
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/documents"
        element={
          <ProtectedRoute>
            <ProjectDocumentsPage />
          </ProtectedRoute>
        }
      />
      {/* FE-1 / LAB-FE-1 — the Test Register is PROJECT-SCOPED, unlike the
          global /inspection-register, because the route that serves it is
          /projects/{project_id}/test-requests: the project is the address,
          not a query string. */}
      <Route
        path="/projects/:projectId/test-register"
        element={
          <ProtectedRoute>
            <TestRegisterPage />
          </ProtectedRoute>
        }
      />
      {/* FE-2 / LAB-FE-1 — one Test Request and its acts. Scoped to the
          project because every route that serves it is
          /projects/{project_id}/test-requests/{id} and the fence is resolved
          from that path. */}
      <Route
        path="/projects/:projectId/test-requests/:requestId"
        element={
          <ProtectedRoute>
            <TestRequestDetailPage />
          </ProtectedRoute>
        }
      />
      {/* QMP-FE-10 — Quality Setup (mockup v79), the project's ONE QMP surface.
          ProtectedRoute and not AdminRoute on purpose: the GET is
          `min_role="viewer"`, because a rule you may not read is not a rule
          you can follow. The write doors are the server's to refuse. */}
      <Route
        path="/projects/:projectId/quality-setup"
        element={
          <ProtectedRoute>
            <QualitySetupPage />
          </ProtectedRoute>
        }
      />
      {/* The two addresses the QMP had before QMP-FE-10 are KEPT as
          compatibility entries: `/qmp` was the v8 page and `-v2` was this
          wizard while the two lived side by side. Links and bookmarks from
          before must not fall through to the dashboard. */}
      <Route
        path="/projects/:projectId/qmp"
        element={
          <ProtectedRoute>
            <QualitySetupRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/quality-setup-v2"
        element={
          <ProtectedRoute>
            <QualitySetupRedirect />
          </ProtectedRoute>
        }
      />
      {/* QWP v1.3 (regra 2) — THE QWP PAGE, the canonical address of the bench.
          The screen is addressed by the PACKAGE now, not by the generation: a
          package is live for the whole project and outlives any number of
          generations, so a URL naming one of them names a moment rather than a
          thing. The page resolves the package's open generation itself. */}
      <Route
        path="/projects/:projectId/qwps/:qwpId"
        element={
          <ProtectedRoute>
            <QualityBreakdownReviewPage />
          </ProtectedRoute>
        }
      />
      {/* The generation-addressed route is KEPT as a compatibility entry: links
          and bookmarks from before v1.3 must not 404, and the AI/geometry flow
          still navigates by generation id. The page redirects to the package's
          address once it knows which package the generation belongs to. */}
      <Route
        path="/projects/:projectId/quality-breakdown/generations/:generationId"
        element={
          <ProtectedRoute>
            <QualityBreakdownReviewPage />
          </ProtectedRoute>
        }
      />
      {/* QBGEO-2g.2 — read-only geometry review: proposals plotted over the
          rendered source page. Guarded like its sibling; backend flattens a
          cross-tenant read to 404. */}
      <Route
        path="/projects/:projectId/quality-breakdown/generations/:generationId/geometry"
        element={
          <ProtectedRoute>
            <QbGeometryReviewPage />
          </ProtectedRoute>
        }
      />
      {/* LC7 — the handover surface: the project's inspections and the punch
          register they produce. Guarded exactly like its siblings; every read
          behind it is min_role="viewer" server-side, so a viewer who reaches
          this URL sees the register and no write button. */}
      <Route
        path="/projects/:projectId/ncrs"
        element={
          <ProtectedRoute>
            <NcrListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/rfis"
        element={
          <ProtectedRoute>
            <RfiListPage />
          </ProtectedRoute>
        }
      />
      {/* NAV-FE-4b — Admin › Project settings (docs/design/project-settings.md).
          Per project: the guard is the project role (the page) and the
          server's `admin` scope on both doors, not TenantAdminRoute. */}
      <Route
        path="/projects/:projectId/settings"
        element={
          <ProtectedRoute>
            <ProjectSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/handover"
        element={
          <ProtectedRoute>
            <HandoverPage />
          </ProtectedRoute>
        }
      />
      {/* H-FE-1 — the Handover Inspection page (prototipo-handover-v2) and
          the punch item page. The old `/execute` address of an inspection
          redirects to the page, so links already sent keep working. */}
      <Route
        path="/projects/:projectId/handover/inspections/:inspectionId"
        element={
          <ProtectedRoute>
            <HandoverInspectionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/handover/inspections/:inspectionId/execute"
        element={
          <ProtectedRoute>
            <HandoverInspectionExecuteRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/handover/punch/:punchItemId"
        element={
          <ProtectedRoute>
            <PunchItemPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId"
        element={
          <ProtectedRoute>
            <ProjectDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/itp-templates"
        element={
          <ProtectedRoute>
            <StructuredITPsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/itp-templates/:templateId"
        element={
          <ProtectedRoute>
            <StructuredITPTemplateEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/drawings/:drawingId"
        element={
          <ProtectedRoute>
            <DrawingViewerPage />
          </ProtectedRoute>
        }
      />
      {/* NL22-A — `/inspections/new`, `/inspections/list` and
          `/inspections/:inspectionId` are REMOVED with their pages. `/inspections`
          below is the living bookings surface and keeps its route. */}
      <Route
        path="/inspections"
        element={
          <ProtectedRoute>
            <InspectionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inspection-register"
        element={
          <ProtectedRoute>
            <InspectionRegisterPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/structured-itcs/:itcId"
        element={
          <ProtectedRoute>
            <StructuredITCDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ncrs/new"
        element={
          <ProtectedRoute>
            <NcrRaisePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ncrs/:ncrId"
        element={
          <ProtectedRoute>
            <NcrPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assets/:elementId"
        element={
          <ProtectedRoute>
            <AssetPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assets/:elementId/dossier"
        element={
          <ProtectedRoute>
            <AssetDossierPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assets/:assetId/itp/:itpId"
        element={
          <ProtectedRoute>
            <ITPDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lots/:lotId"
        element={
          <ProtectedRoute>
            <LotDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/asset-qa-table"
        element={
          <ProtectedRoute>
            <AssetQATablePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <TenantAdminRoute>
            <UserManagementPage />
          </TenantAdminRoute>
        }
      />
      <Route
        path="/users/invite"
        element={
          <TenantAdminRoute>
            <InviteUserPage />
          </TenantAdminRoute>
        }
      />
      <Route
        path="/companies"
        element={
          <PlatformOwnerRoute>
            <CompaniesManagementPage />
          </PlatformOwnerRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Communication System */}
      <Route
        path="/threads/:threadId"
        element={
          <ProtectedRoute>
            <ThreadDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rfis/:rfiId"
        element={
          <ProtectedRoute>
            <EntityThreadRedirect entityType="rfi" paramName="rfiId" />
          </ProtectedRoute>
        }
      />

      {/* Mobile Field Interface Routes */}
      <Route path="/m/login" element={<MobilePublicRoute><MobileLoginPage /></MobilePublicRoute>} />
      <Route path="/m" element={<MobileProtectedRoute><MobileHomePage /></MobileProtectedRoute>} />
      <Route path="/m/lotmaps" element={<MobileProtectedRoute><MobileLotMapsPage /></MobileProtectedRoute>} />
      <Route path="/m/lotmap/:drawingId" element={<MobileProtectedRoute><MobileLotMapViewPage /></MobileProtectedRoute>} />
      <Route path="/m/asset/:elementId" element={<MobileProtectedRoute><MobileAssetPage /></MobileProtectedRoute>} />
      <Route path="/m/asset/:elementId/photos" element={<MobileProtectedRoute><MobilePhotoUploadPage /></MobileProtectedRoute>} />

      {/* Redirect root to login or dashboard */}
      <Route path="/" element={<Navigate to="/login" replace />} />

      {/* External token-based access (Phase 1B) — no auth wrapper */}
      <Route path="/ext/:token" element={<ExternalThreadPage />} />

      {/* LAB-FE-1 B6 — the laboratory portal, PUBLIC: the permanent link the
          laboratory's e-mail carries (`{APP_URL}/portal/{token}`), then an
          authorised e-mail and a one-time code. No ProtectedRoute: the
          laboratory has no account; its session is `X-Portal-Token`. */}
      <Route path="/portal/:linkToken" element={<LabPortalPage />} />
      <Route path="/portal/:linkToken/requests/:requestId" element={<LabPortalRequestPage />} />

      {/* 404 - Redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ProjectProvider>
          <MobileRedirect>
            <AppRoutes />
          </MobileRedirect>
        </ProjectProvider>
        <Toaster
          position="top-center"
          toastOptions={{
            style: {
              background: '#1e293b',
              border: '1px solid #334155',
              color: '#f8fafc',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
